import React from 'react'
import { useForm } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type CrmCoreTeam from '@/types/serializers/CrmCoreTeam'
import type CrmFundraisingCampaign from '@/types/serializers/CrmFundraisingCampaign'
import {
  TeamGroupedUserPicker,
  type AssignableTeam,
} from './TeamGroupedUserPicker'

interface EditCampaignFormProps {
  campaign: CrmFundraisingCampaign
  teams: CrmCoreTeam[]
  assignableTeams: AssignableTeam[]
  onDone: () => void
}

interface CampaignFormData {
  campaign: {
    name: string
    description: string
    campaign_type: string
    goal_amount_millimes: string
    start_date: string
    end_date: string
    team_ids: number[]
    user_ids: number[]
  }
}

const CAMPAIGN_TYPES = [
  { value: 'fundraising', label: 'Collecte' },
  { value: 'awareness', label: 'Sensibilisation' },
  { value: 'event', label: 'Evenement' },
  { value: 'emergency', label: 'Urgence' },
]

export function EditCampaignForm({
  campaign,
  teams,
  assignableTeams,
  onDone,
}: EditCampaignFormProps) {
  const form = useForm<CampaignFormData>({
    campaign: {
      name: campaign.name,
      description: campaign.description ?? '',
      campaign_type: campaign.campaign_type_key,
      goal_amount_millimes: String(campaign.goal_amount_millimes ?? ''),
      start_date: campaign.start_date ?? '',
      end_date: campaign.end_date ?? '',
      team_ids: campaign.team_ids ?? [],
      user_ids: campaign.user_ids ?? [],
    },
  })

  function handleTeamToggle(teamId: number) {
    const current = form.data.campaign.team_ids
    form.setData('campaign', {
      ...form.data.campaign,
      team_ids: current.includes(teamId)
        ? current.filter((id) => id !== teamId)
        : [...current, teamId],
    })
  }

  function handleUserToggle(userId: number) {
    const current = form.data.campaign.user_ids
    form.setData('campaign', {
      ...form.data.campaign,
      user_ids: current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.patch(`/fundraising/campaigns/${campaign.id}`, {
      onSuccess: () => onDone(),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      <div className="space-y-2">
        <Label htmlFor="edit_camp_name">Nom *</Label>
        <Input
          id="edit_camp_name"
          value={form.data.campaign.name}
          onChange={(e) =>
            form.setData('campaign', {
              ...form.data.campaign,
              name: e.target.value,
            })
          }
          required
        />
        {form.errors['campaign.name'] && (
          <p className="text-sm text-destructive">
            {form.errors['campaign.name']}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit_camp_desc">Description</Label>
        <Input
          id="edit_camp_desc"
          value={form.data.campaign.description}
          onChange={(e) =>
            form.setData('campaign', {
              ...form.data.campaign,
              description: e.target.value,
            })
          }
        />
      </div>

      <div className="space-y-2">
        <Label>Type *</Label>
        <Select
          value={form.data.campaign.campaign_type}
          onValueChange={(val) =>
            form.setData('campaign', {
              ...form.data.campaign,
              campaign_type: val,
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Choisir un type" />
          </SelectTrigger>
          <SelectContent>
            {CAMPAIGN_TYPES.map((ct) => (
              <SelectItem key={ct.value} value={ct.value}>
                {ct.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit_camp_goal">Objectif (millimes)</Label>
        <Input
          id="edit_camp_goal"
          type="number"
          min="0"
          value={form.data.campaign.goal_amount_millimes}
          onChange={(e) =>
            form.setData('campaign', {
              ...form.data.campaign,
              goal_amount_millimes: e.target.value,
            })
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit_camp_start">Date de debut</Label>
        <Input
          id="edit_camp_start"
          type="date"
          value={form.data.campaign.start_date}
          onChange={(e) =>
            form.setData('campaign', {
              ...form.data.campaign,
              start_date: e.target.value,
            })
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit_camp_end">Date de fin</Label>
        <Input
          id="edit_camp_end"
          type="date"
          value={form.data.campaign.end_date}
          onChange={(e) =>
            form.setData('campaign', {
              ...form.data.campaign,
              end_date: e.target.value,
            })
          }
        />
      </div>

      {/* Team multi-select */}
      {teams && teams.length > 0 && (
        <div className="space-y-2">
          <Label>Equipes</Label>
          <div className="space-y-1 rounded-md border p-3 max-h-40 overflow-y-auto">
            {teams.map((team) => (
              <label
                key={team.id}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={form.data.campaign.team_ids.includes(team.id)}
                  onChange={() => handleTeamToggle(team.id)}
                  className="rounded border-gray-300"
                />
                {team.name}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Individual user multi-select, grouped by team */}
      <TeamGroupedUserPicker
        assignableTeams={assignableTeams}
        selectedIds={form.data.campaign.user_ids}
        onToggle={handleUserToggle}
      />

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={form.processing} className="flex-1">
          {form.processing ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Annuler
        </Button>
      </div>
    </form>
  )
}
